import type { Canvas, FabricObject } from "fabric";
import { RingBuffer } from "../../../../../../collections/ringBuffer.ts";
import type { RgbaColor } from "../../../../../../types.ts";
import { rgbaToString } from "../../../../../../utils/colors.ts";
import {
  type BaseCanvasState,
  createCanvasStore,
} from "../../components/FabricCanvas/canvasStoreFactory.ts";
import {
  ASSET_LAYER_TYPE,
  CIRCLE_SHAPE_TYPE,
  IMAGE_LAYER_TYPE,
  LINE_LAYER_TYPE,
  RECT_SHAPE_TYPE,
  SHAPE_LAYER_TYPE,
  TEXT_LAYER_TYPE,
  TRIANGLE_SHAPE_TYPE,
} from "../constants.ts";
import { fabricObjectToLayer } from "../typeguard.ts";
import type {
  AssetLayer,
  CanvasSize,
  CircleShapeLayerProperties,
  ImageLayer,
  Layer,
  LineLayer,
  LineLayerProperties,
  ShapeLayer,
  ShapeType,
  SquareShapeLayerProperties,
  TextLayer,
  TextLayerProperties,
  TriangleShapeLayerProperties,
} from "../types.ts";
import { createAssetLayer } from "../utils/asset.ts";
import { computeCanvasContainTransform } from "../utils/canvas.ts";
import { createImageLayer, duplicateImageLayer } from "../utils/image.ts";
import {
  deleteLayer,
  getActiveLayers,
  getLayers,
  scaleAndTranslateLayers,
  setActiveLayers,
} from "../utils/layer.ts";
import { createLineLayer, duplicateLineLayer } from "../utils/line.ts";
import { deserializeCanvas, serializeCanvas } from "../utils/serialization.ts";
import {
  convertShapeLayer,
  createCircleShapeLayer,
  createSquareShapeLayer,
  createTriangleShapeLayer,
  duplicateShapeLayer,
} from "../utils/shape.ts";
import { createTextLayer, duplicateTextLayer } from "../utils/text.ts";

const MAX_HISTORY_SIZE = 50;
const SNASPHOT_DEBOUNCE_TIME = 100;

type HistorySnapshot = {
  // FIXME: Improve type of JSON canvas serialization to avoid using any
  state: any;
  id: string;
};

export type DesignerCanvasState = BaseCanvasState & {
  canvasSize: CanvasSize | undefined;
  selectedLayerIds: string[];
  backgroundColor: RgbaColor | undefined;
  clipboard: Layer[];
  isPasteDisabled: boolean;
  isTextEditing: boolean;
  isVideoDisplayed: boolean;
  isVideoPlaying: boolean;
  zoomLevel: number;
  history: RingBuffer<HistorySnapshot>;
  historyProcessing: boolean;
  historyNextSnapshot: HistorySnapshot | undefined;
  snapshotBlockedTimeout: ReturnType<typeof setTimeout> | null;
};

export type DesignerCanvasActions = {
  removeLayer: (layer: Layer) => void;
  toggleLayerVisibility: (layerId: string) => void;
  setLayerName: (layerId: string, name: string) => void;
  selectLayers: (layerIds: string[], active?: boolean) => void;
  deselectLayers: () => void;
  getLayerByFabricObject: (fabricObject: FabricObject) => Layer | undefined;
  addTextLayer: (properties?: TextLayerProperties) => TextLayer | undefined;
  addImageLayer: (contentUuid: string) => ImageLayer | undefined;
  addLineLayer: (options?: LineLayerProperties) => LineLayer | undefined;
  addShapeLayer: (shapeType: ShapeType) => ShapeLayer | undefined;
  addAssetLayer: (assetUuid: string) => AssetLayer | undefined;
  convertShapeLayerTo: (shapeLayer: ShapeLayer, shapeType: ShapeType) => void;
  serialize: () => any;
  deserialize: ({
    saved,
    defaultCanvasSize,
  }: {
    saved: any;
    defaultCanvasSize?: CanvasSize;
  }) => Promise<void>;
  setCanvasSize: (canvasSize?: CanvasSize, autoLayout?: boolean) => void;
  copy: () => Promise<void>;
  paste: () => Layer[] | undefined;
  reset: () => void;
  reorderLayers: (sourceLayerId: string, targetLayerId: string) => void;
  setIsVideoDisplayed: (isVideoDisplayed: boolean) => void;
  setIsVideoPlaying: (isVideoPlaying: boolean) => void;
  setIsPasteDisabled: (isDisabled: boolean) => void;
  setIsTextEditing: (isEditing: boolean) => void;
  setZoomLevel: (zoomLevel: number) => void;
  setBackgroundColor: (color: RgbaColor) => void;
  clearHistory: () => void;
  undo: (id: string) => Promise<void>;
  redo: (id: string) => Promise<void>;
  saveHistorySnapshot: (id: string) => void;
};

export const designerCanvasStore = createCanvasStore<
  DesignerCanvasState,
  DesignerCanvasActions
>(
  {
    fabricCanvas: undefined as Canvas | undefined,
    isDrawingMode: false,
    isDeletionDisabled: false,
    isPasteDisabled: false,
    canvasSize: undefined,
    selectedLayerIds: [],
    clipboard: [] as Layer[],
    isVideoDisplayed: false,
    isVideoPlaying: false,
    isTextEditing: false,
    zoomLevel: 1,
    backgroundColor: undefined as RgbaColor | undefined,
    history: new RingBuffer<HistorySnapshot>(MAX_HISTORY_SIZE),
    historyProcessing: false,
    historyNextSnapshot: undefined,
    snapshotBlockedTimeout: null,
  },
  (utils) => {
    const { get, set } = utils;

    const startSnapshotBlocking = () => {
      const { snapshotBlockedTimeout } = get();

      if (snapshotBlockedTimeout !== null) clearTimeout(snapshotBlockedTimeout);

      const timeout = setTimeout(() => {
        set({ historyProcessing: false, snapshotBlockedTimeout: null });
      }, SNASPHOT_DEBOUNCE_TIME);

      set({ snapshotBlockedTimeout: timeout });
    };

    const reset = () => {
      const { fabricCanvas } = get();
      if (fabricCanvas) fabricCanvas.clear();
      set({
        isDrawingMode: false,
        isDeletionDisabled: false,
        backgroundColor: undefined,
        canvasSize: undefined,
      });
    };

    const selectLayers = (layerIds: string[], active = true) => {
      const currentState = get();
      set({ selectedLayerIds: layerIds });
      if (active && currentState.fabricCanvas) {
        setActiveLayers({
          fabricCanvas: currentState.fabricCanvas,
          activeLayerIds: layerIds,
        });
      }
    };

    const deselectLayers = () => {
      const currentState = get();
      set({ selectedLayerIds: [] });
      if (currentState.fabricCanvas) {
        currentState.fabricCanvas.discardActiveObject();
        currentState.fabricCanvas.renderAll();
      }
    };

    const addTextLayer = (properties?: TextLayerProperties) => {
      const currentState = get();
      if (!currentState.fabricCanvas || !currentState.canvasSize) return;

      const textLayer = createTextLayer({
        fabricCanvas: currentState.fabricCanvas,
        canvasSize: currentState.canvasSize,
        properties: properties ?? {},
      });

      return textLayer;
    };

    const removeLayer = (layer: Layer) => {
      const currentState = get();
      if (!currentState.fabricCanvas) return;
      deleteLayer(currentState.fabricCanvas, layer);

      set({
        selectedLayerIds: currentState.selectedLayerIds.filter(
          (id) => id !== layer.id,
        ),
      });
    };

    const addSquareShapeLayer = ({
      options,
    }: {
      options?: SquareShapeLayerProperties;
    }) => {
      const currentState = get();
      if (!currentState.fabricCanvas || !currentState.canvasSize) return;

      const newShapeLayer = createSquareShapeLayer({
        fabricCanvas: currentState.fabricCanvas,
        canvasSize: currentState.canvasSize,
        options: options ?? {},
      });

      return newShapeLayer;
    };

    const addCircleShapeLayer = ({
      options,
    }: {
      options?: CircleShapeLayerProperties;
    }) => {
      const currentState = get();
      if (!currentState.fabricCanvas || !currentState.canvasSize) return;

      const newShapeLayer = createCircleShapeLayer({
        fabricCanvas: currentState.fabricCanvas,
        canvasSize: currentState.canvasSize,
        options: options ?? {},
      });

      return newShapeLayer;
    };

    const addTriangleShapeLayer = ({
      options,
    }: {
      options?: TriangleShapeLayerProperties;
    }) => {
      const currentState = get();
      if (!currentState.fabricCanvas || !currentState.canvasSize) return;

      const newShapeLayer = createTriangleShapeLayer({
        fabricCanvas: currentState.fabricCanvas,
        canvasSize: currentState.canvasSize,
        options: options ?? {},
      });

      return newShapeLayer;
    };

    const serialize = () => {
      const {
        fabricCanvas,
        isDrawingMode,
        isDeletionDisabled,
        selectedLayerIds,
        backgroundColor,
        canvasSize,
      } = get();
      if (!fabricCanvas) return undefined;
      return serializeCanvas({
        fabricCanvas,
        canvasState: {
          isDrawingMode,
          isDeletionDisabled,
          selectedLayerIds,
          backgroundColor,
          canvasSize,
        },
      });
    };

    const deserialize = async ({ saved }: { saved: any }) => {
      const { fabricCanvas } = get();
      if (!fabricCanvas) return;

      await deserializeCanvas({
        saved,
        fabricCanvas,
        setCanvasState: set,
      });
    };

    return {
      selectLayers,
      deselectLayers,
      addTextLayer,
      removeLayer,
      reset,
      serialize,
      deserialize,
      setIsPasteDisabled: (isDisabled: boolean) => {
        set({ isPasteDisabled: isDisabled });
      },

      setIsTextEditing: (isEditing: boolean) => {
        set({ isTextEditing: isEditing });
      },

      toggleLayerVisibility: (layerId: string) => {
        const currentState = get();
        if (!currentState.fabricCanvas) return;
        const layer = getLayers(currentState.fabricCanvas).find(
          (l) => l.id === layerId,
        );
        if (!layer) return;
        layer.visible = !layer.visible;
        if (layer.canvas) {
          layer.canvas.renderAll();

          // XXX: Fire a custom event on the canvas to notify the listeners that the layer visibility has changed
          layer.canvas.fire("object:visibility:changed", {
            target: layer,
            visible: layer.visible,
            layerId,
          });
        }
      },

      setLayerName: (layerId: string, name: string) => {
        const currentState = get();
        if (!currentState.fabricCanvas) return;
        const layer = getLayers(currentState.fabricCanvas).find(
          (l) => l.id === layerId,
        );
        if (!layer) return;
        layer.name = name;
        if (layer.canvas) layer.canvas.fire("layer:renamed");
      },

      getLayerByFabricObject: (fabricObject: FabricObject) => {
        const currentState = get();
        const layer = fabricObjectToLayer(fabricObject);
        if (!layer || !currentState.fabricCanvas) return undefined;
        return getLayers(currentState.fabricCanvas).find((l) => l === layer);
      },

      addImageLayer: (contentUuid: string) => {
        const currentState = get();
        if (!currentState.fabricCanvas || !currentState.canvasSize) return;

        const imageLayer = createImageLayer({
          fabricCanvas: currentState.fabricCanvas,
          contentUuid,
          canvasSize: currentState.canvasSize,
          options: {},
        });
        return imageLayer;
      },

      addLineLayer: (options?: LineLayerProperties) => {
        const currentState = get();
        if (!currentState.fabricCanvas || !currentState.canvasSize) return;

        const lineLayer = createLineLayer({
          fabricCanvas: currentState.fabricCanvas,
          canvasSize: currentState.canvasSize,
          options: options ?? {},
        });

        return lineLayer;
      },

      addShapeLayer: (shapeType: ShapeType) => {
        switch (shapeType) {
          case RECT_SHAPE_TYPE:
            return addSquareShapeLayer({
              options: { width: 100, height: 100 },
            });
          case CIRCLE_SHAPE_TYPE:
            return addCircleShapeLayer({
              options: { radius: 50 },
            });
          case TRIANGLE_SHAPE_TYPE:
            return addTriangleShapeLayer({
              options: { width: 100, height: 100 },
            });
        }
      },

      addAssetLayer: (assetUuid: string) => {
        const currentState = get();
        if (!currentState.fabricCanvas || !currentState.canvasSize) return;
        const assetLayer = createAssetLayer({
          fabricCanvas: currentState.fabricCanvas,
          canvasSize: currentState.canvasSize,
          assetUuid,
        });

        return assetLayer;
      },

      setCanvasSize: (canvasSize?: CanvasSize, autoLayout = false) => {
        const { fabricCanvas, canvasSize: currentCanvasSize } = get();
        if (!fabricCanvas) return;

        if (currentCanvasSize && canvasSize && autoLayout) {
          const { scale, offsetX, offsetY } = computeCanvasContainTransform({
            initialSize: currentCanvasSize,
            targetSize: canvasSize,
          });
          scaleAndTranslateLayers(fabricCanvas, getLayers(fabricCanvas), {
            scale,
            translateX: offsetX,
            translateY: offsetY,
          });
        }
        set({ canvasSize });

        fabricCanvas.fire("canvas:size:changed");
        fabricCanvas.renderAll();
      },

      reorderLayers: (sourceLayerId: string, targetLayerId: string) => {
        const currentState = get();
        if (!currentState.fabricCanvas) return;
        const layers = getLayers(currentState.fabricCanvas);
        const sourceLayer = layers.find((l) => l.id === sourceLayerId);
        const targetLayer = layers.find((l) => l.id === targetLayerId);

        if (!sourceLayer || !targetLayer || sourceLayer.id === targetLayer.id) {
          return;
        }
        const sourceIndex = layers.findIndex((l) => l.id === sourceLayerId);
        const targetIndex = layers.findIndex((l) => l.id === targetLayerId);

        layers.splice(sourceIndex, 1);
        layers.splice(targetIndex, 0, sourceLayer);

        const minIndex = Math.min(sourceIndex, targetIndex);
        const maxIndex = Math.max(sourceIndex, targetIndex);

        for (let i = minIndex; i <= maxIndex; i++) {
          layers[i].positionIndex = layers.length - 1 - i;
        }

        const fabricTargetIndex = layers.length - 1 - targetIndex;
        currentState.fabricCanvas.moveObjectTo(sourceLayer, fabricTargetIndex);
        currentState.fabricCanvas.renderAll();
        currentState.fabricCanvas.fire("canvas:objects:reordered");
      },

      copy: async () => {
        const currentState = get();
        const fabricCanvas = currentState.fabricCanvas;
        if (!fabricCanvas) return;

        const activeLayers = getActiveLayers({
          fabricCanvas: currentState.fabricCanvas,
        });
        if (activeLayers.length === 0) return;
        const clonePromises: Promise<Layer>[] = [];

        for (const layer of activeLayers) {
          const cloned = layer.clone();
          clonePromises.push(cloned);
        }

        const clipboard = await Promise.all(clonePromises);
        set({ clipboard });
      },

      paste: () => {
        const currentState = get();
        if (
          !currentState.fabricCanvas ||
          currentState.clipboard.length === 0 ||
          currentState.isPasteDisabled ||
          !currentState.canvasSize
        ) {
          return;
        }
        const newLayers: Layer[] = [];

        for (const obj of currentState.clipboard) {
          switch (obj.layerType) {
            case TEXT_LAYER_TYPE:
              const textLayer = duplicateTextLayer(
                currentState.fabricCanvas,
                currentState.canvasSize,
                obj,
              );
              newLayers.push(textLayer);
              break;
            case IMAGE_LAYER_TYPE:
              const imageLayer = duplicateImageLayer(
                currentState.fabricCanvas,
                currentState.canvasSize,
                obj,
              );
              newLayers.push(imageLayer);
              break;
            case SHAPE_LAYER_TYPE:
              const shapeLayer = duplicateShapeLayer(
                currentState.fabricCanvas,
                currentState.canvasSize,
                obj,
              );
              newLayers.push(shapeLayer);
              break;
            case LINE_LAYER_TYPE:
              const lineLayer = duplicateLineLayer(
                currentState.fabricCanvas,
                currentState.canvasSize,
                obj,
              );
              newLayers.push(lineLayer);
              break;
            case ASSET_LAYER_TYPE:
              break;
          }
        }
        return newLayers;
      },

      convertShapeLayerTo: (
        shapeLayer: ShapeLayer,
        newShapeType: ShapeType,
      ) => {
        const currentState = get();
        if (!currentState.fabricCanvas || !currentState.canvasSize) return;

        // FIXME: The rotation property is not supported yet and won't be preserved when converting shape to another shape type.
        const newShapeLayer = convertShapeLayer(
          currentState.fabricCanvas,
          currentState.canvasSize,
          shapeLayer,
          newShapeType,
        );

        if (newShapeLayer) selectLayers([newShapeLayer.id]);
        removeLayer(shapeLayer);
      },

      setIsVideoDisplayed: (isVideoDisplayed: boolean) => {
        set({ isVideoDisplayed });
      },

      setIsVideoPlaying: (isVideoPlaying: boolean) => {
        set({ isVideoPlaying });
      },

      setZoomLevel: (zoomLevel: number) => {
        set({ zoomLevel: Math.max(0.1, Math.min(5, zoomLevel)) });
      },

      setBackgroundColor: (color: RgbaColor) => {
        const { fabricCanvas } = get();
        if (!fabricCanvas) return;

        fabricCanvas.backgroundColor = rgbaToString(color);
        fabricCanvas.fire("background:color:changed");

        fabricCanvas.renderAll();
        set({
          backgroundColor: color,
        });
      },

      clearHistory: () => {
        const { history } = get();
        history.clear();
        set({ history: new RingBuffer<HistorySnapshot>(MAX_HISTORY_SIZE) });
      },

      undo: async (id: string): Promise<void> => {
        const { fabricCanvas, history, historyProcessing } = get();

        if (!fabricCanvas || historyProcessing) return;

        try {
          set({ historyProcessing: true });

          const snapshot = history.undo();
          if (!snapshot || snapshot.id !== id) {
            set({ historyProcessing: false });
            return;
          }

          await deserialize({ saved: snapshot.state });

          // FIXME: Start snapshot blocking for some time after history processing is done to avoid snapshotting result of the history operation.
          // Could be avoided by performing JSON comparisons when saving new snapshots
          startSnapshotBlocking();
        } catch (e) {
          console.error("Undo failed:", e);
          set({ historyProcessing: false });
        }
      },

      redo: async (id: string): Promise<void> => {
        const { fabricCanvas, history, historyProcessing } = get();

        if (!fabricCanvas || historyProcessing) return;

        try {
          set({ historyProcessing: true });

          const snapshot = history.redo();
          if (!snapshot || snapshot.id !== id) {
            set({ historyProcessing: false });
            return;
          }

          await deserialize({ saved: snapshot.state });

          // FIXME: Start snapshot blocking for some time after history processing is done to avoid snapshotting result of the history operation.
          // Could be avoided by performing JSON comparisons when saving new snapshots
          startSnapshotBlocking();
        } catch (e) {
          console.error("Redo failed:", e);
          set({ historyProcessing: false });
        }
      },

      saveHistorySnapshot: (id: string) => {
        const { historyProcessing, history, historyNextSnapshot } = get();
        if (historyProcessing) return;

        // XXX: When manipulating snapshots history we should track the "pre-mutation" state, before Fabric mutates it. However as we
        //  can only listen to events, we can only track the "post-mutation" state. To fix this fabric issue, we store the next
        //  snapshot in the state and push it whenever new changes are made.
        if (!historyNextSnapshot) {
          set({ historyNextSnapshot: { state: serialize(), id } });
          return;
        }

        const json = serialize();
        if (!json) return;

        history.push(historyNextSnapshot);
        set({ historyNextSnapshot: { state: json, id } });
      },
    };
  },
);
